import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import {
  getOrders,
  updateDeliveryStatus,
  Order,
} from '@/lib/orders-service';
import type { DeliveryStatus } from '@/types/orders';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Truck, MapPin, Phone, User, Package, Camera, Clock, ArrowRight } from 'lucide-react';

export const Route = createFileRoute('/_auth/operator/deliveries')({
  component: DeliveriesPage,
});

// ============================================================================
// Helpers
// ============================================================================

function getDeliveryBadgeVariant(status: DeliveryStatus): 'pending' | 'inProgress' | 'confirmed' | 'completed' {
  const map: Record<DeliveryStatus, 'pending' | 'inProgress' | 'confirmed' | 'completed'> = {
    PENDING: 'pending',
    OUT_FOR_DELIVERY: 'inProgress',
    ARRIVED: 'confirmed',
    DELIVERED: 'completed',
  };
  return map[status];
}

const DELIVERY_COLUMNS: { status: DeliveryStatus; color: string }[] = [
  { status: 'PENDING', color: 'border-amber-400' },
  { status: 'OUT_FOR_DELIVERY', color: 'border-blue-400' },
  { status: 'ARRIVED', color: 'border-violet-400' },
  { status: 'DELIVERED', color: 'border-emerald-400' },
];

function getNextStatus(current: DeliveryStatus): DeliveryStatus | null {
  const map: Record<DeliveryStatus, DeliveryStatus | null> = {
    PENDING: 'OUT_FOR_DELIVERY',
    OUT_FOR_DELIVERY: 'ARRIVED',
    ARRIVED: 'DELIVERED',
    DELIVERED: null,
  };
  return map[current];
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
}

function formatTime(date: Date) {
  return new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// ============================================================================
// Delivery Card
// ============================================================================

interface DeliveryCardProps {
  order: Order;
  onAdvance: (orderId: string, current: DeliveryStatus) => void;
  t: (key: string) => string;
}

function DeliveryCard({ order, onAdvance, t }: DeliveryCardProps) {
  const next = order.deliveryStatus ? getNextStatus(order.deliveryStatus) : null;
  const actionLabels: Record<DeliveryStatus, string> = {
    PENDING: '',
    OUT_FOR_DELIVERY: t('deliveries.dispatchDelivery'),
    ARRIVED: t('deliveries.markArrived'),
    DELIVERED: t('deliveries.markDelivered'),
  };

  return (
    <Card className="mb-3 hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-3">
        {/* Header: order number + total */}
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-semibold">{order.orderNumber}</span>
          <span className="font-semibold text-sm">{formatCurrency(order.total)}</span>
        </div>

        {/* Customer */}
        <div className="flex items-center gap-2 text-sm">
          <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{order.customerName}</span>
          {order.customerPhone && (
            <a
              href={`tel:${order.customerPhone}`}
              className="text-muted-foreground hover:text-primary ml-auto shrink-0"
            >
              <Phone className="h-3.5 w-3.5" />
            </a>
          )}
        </div>

        {/* Address */}
        {order.deliveryAddress && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{order.deliveryAddress}</span>
          </div>
        )}

        {/* Delivery notes */}
        {order.deliveryNotes && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
            {order.deliveryNotes}
          </div>
        )}

        {/* Portaria contact */}
        {order.deliveryContactName && (
          <div className="flex items-center gap-2 text-xs border rounded px-2 py-1.5 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <Phone className="h-3 w-3 text-blue-600 shrink-0" />
            <span className="font-medium text-blue-700 dark:text-blue-400">{order.deliveryContactName}</span>
            {order.deliveryContactPhone && (
              <a href={`tel:${order.deliveryContactPhone}`} className="text-blue-600 hover:underline ml-auto text-xs">
                {order.deliveryContactPhone}
              </a>
            )}
          </div>
        )}

        {/* Items summary */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Package className="h-3 w-3" />
          <span>{order.lineItems.length} {order.lineItems.length === 1 ? 'item' : 'itens'}</span>
          <span className="text-muted-foreground/50">|</span>
          <Clock className="h-3 w-3" />
          <span>{formatTime(order.createdAt)}</span>
        </div>

        {/* Proof of delivery photo */}
        {order.deliveryStatus === 'DELIVERED' && (
          <div className="text-xs">
            {order.deliveryProofImageUrl ? (
              <a
                href={order.deliveryProofImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-emerald-600 hover:underline"
              >
                <Camera className="h-3 w-3" />
                {t('deliveries.proofOfDelivery')}
              </a>
            ) : (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Camera className="h-3 w-3" />
                {t('deliveries.noProofYet')}
              </span>
            )}
          </div>
        )}

        {/* Timestamps */}
        {order.deliveryDispatchedAt && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <ArrowRight className="h-3 w-3" />
            Saiu: {formatTime(order.deliveryDispatchedAt)}
            {order.deliveryArrivedAt && <> | Chegou: {formatTime(order.deliveryArrivedAt)}</>}
            {order.deliveryCompletedAt && <> | Entregue: {formatTime(order.deliveryCompletedAt)}</>}
          </div>
        )}

        {/* Action button */}
        {next && (
          <Button
            size="sm"
            className="w-full"
            variant={next === 'DELIVERED' ? 'default' : 'outline'}
            onClick={() => onAdvance(order.id, order.deliveryStatus!)}
          >
            {next === 'OUT_FOR_DELIVERY' && <Truck className="h-4 w-4 mr-1.5" />}
            {next === 'ARRIVED' && <MapPin className="h-4 w-4 mr-1.5" />}
            {next === 'DELIVERED' && <Camera className="h-4 w-4 mr-1.5" />}
            {next === 'DELIVERED' ? t('deliveries.deliverWithProof') : actionLabels[next]}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function DeliveriesPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusiness } = useOperator();

  const [deliveryOrders, setDeliveryOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeliveries = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getOrders({
        businessId: selectedBusiness.id,
        fulfillmentType: 'DELIVERY',
      });
      setDeliveryOrders(response.orders);
    } catch (err) {
      console.error('Failed to fetch deliveries:', err);
      setError('Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handleAdvance = async (orderId: string, currentStatus: DeliveryStatus) => {
    const next = getNextStatus(currentStatus);
    if (!next) return;

    const result = await updateDeliveryStatus(orderId, next);
    if (result.success) {
      await fetchDeliveries();
    } else {
      alert(`Failed: ${result.error}`);
    }
  };

  // Group orders by delivery status
  const grouped = useMemo(() => {
    const groups: Record<DeliveryStatus, Order[]> = {
      PENDING: [],
      OUT_FOR_DELIVERY: [],
      ARRIVED: [],
      DELIVERED: [],
    };
    for (const order of deliveryOrders) {
      const status = order.deliveryStatus || 'PENDING';
      groups[status].push(order);
    }
    return groups;
  }, [deliveryOrders]);

  // Stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const completedToday = grouped.DELIVERED.filter((o) => {
      if (!o.deliveryCompletedAt) return false;
      const d = new Date(o.deliveryCompletedAt);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    }).length;

    return {
      active: grouped.PENDING.length + grouped.OUT_FOR_DELIVERY.length + grouped.ARRIVED.length,
      completedToday,
      awaiting: grouped.PENDING.length,
      inTransit: grouped.OUT_FOR_DELIVERY.length + grouped.ARRIVED.length,
    };
  }, [grouped]);

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!operator) return null;

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-lg font-semibold">{error}</h3>
            <Button onClick={fetchDeliveries} className="mt-4">
              {t('common.error')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('deliveries.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('deliveries.subtitle')} | <strong>{selectedBusiness?.name}</strong>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('deliveries.activeDeliveries')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('deliveries.awaitingDispatch')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.awaiting}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('deliveries.inTransit')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.inTransit}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('deliveries.completedToday')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.completedToday}</div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board */}
      {deliveryOrders.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Truck className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold">{t('deliveries.noDeliveries')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('deliveries.noDeliveriesDescription')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {DELIVERY_COLUMNS.map(({ status, color }) => (
            <div key={status} className={`border-t-4 ${color} rounded-lg`}>
              <div className="flex items-center justify-between px-3 py-2 bg-muted/30 rounded-t-lg">
                <Badge variant={getDeliveryBadgeVariant(status)} className="text-xs">
                  {t(`deliveries.${status === 'OUT_FOR_DELIVERY' ? 'outForDelivery' : status.toLowerCase()}`)}
                </Badge>
                <span className="text-xs font-semibold text-muted-foreground">{grouped[status].length}</span>
              </div>
              <div className="p-2 min-h-[200px]">
                {grouped[status].length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
                    --
                  </div>
                ) : (
                  grouped[status].map((order) => (
                    <DeliveryCard
                      key={order.id}
                      order={order}
                      onAdvance={handleAdvance}
                      t={t}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
